import { useRef, useState } from 'react';
import { Alert, Platform } from 'react-native';

import { messageFor } from '@/services/errors';
import { voltrService } from '@/services/voltr';
import { dealsStore } from '@/stores/deals.store';
import type { Action, StepVideo } from '@/types/deal';

/**
 * Removing a sent-back video on the deal page (its other way out, a
 * replacement, comes from the camera roll: SocialDealBody), after a confirm
 * (the app's native alert, as for cash out). One remove per video at a time:
 * its buttons wait (`removing`) and a second tap is dropped. A web browser
 * that suppresses confirm() answers "no", so nothing happens. An answer that
 * lands after logout is dropped.
 */
export function useSocialVideoActions(action: Action | undefined) {
  const [videoError, setVideoError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ReadonlySet<string>>(() => new Set());
  const inFlight = useRef(new Set<string>());

  function mark(id: string, on: boolean) {
    if (on) inFlight.current.add(id);
    else inFlight.current.delete(id);
    setRemoving(new Set(inFlight.current));
  }

  async function removeNow(video: StepVideo) {
    if (!action || inFlight.current.has(video.id)) return;
    mark(video.id, true);
    // Taken now: a logout (or another login) meanwhile makes the answer no one's.
    const sent = dealsStore.actionMark();
    try {
      const updated = await voltrService.removeVideo(action.id, video.id);
      if (dealsStore.actionFor(action.deal_id)?.id === action.id) dealsStore.applyActionSince(sent, updated);
    } catch (err) {
      setVideoError(messageFor(err, "Couldn't remove that video. Try again."));
    } finally {
      mark(video.id, false);
    }
  }

  function remove(video: StepVideo) {
    if (inFlight.current.has(video.id)) return;
    setVideoError(null);
    const title = `Remove ${video.name}?`;
    const message = "It won't count toward this deal. You can't undo this.";
    if (Platform.OS === 'web') {
      if (window.confirm(`${title}\n${message}`)) void removeNow(video);
      return;
    }
    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => void removeNow(video) },
    ]);
  }

  return { remove, removing, videoError };
}
